import { useMemo, useState } from 'react'

import { useQueries } from '@tanstack/react-query'
import { ColDef } from 'ag-grid-enterprise'
import { isBefore } from 'date-fns'
import { Play, Plus } from 'lucide-react'

import { getTMinus, toISO8601 } from '@fortem-monorepo/js-utilities'
import { createColDefs } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { createCellRenderer } from '@fortem-monorepo/react-ag'
import { HeaderConfigBar, useApi } from '@fortem-monorepo/react-spa'
import {
  Widget,
  WidgetContent,
  WidgetHeaderWithQuery,
  WidgetHeaderWithQueryProps,
} from '@fortem-monorepo/react-spa'
import { Badge } from '@fortem-monorepo/react-ui'
import { Button } from '@fortem-monorepo/react-ui'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'

import { CanAccess } from '@/components/CanAccess'
import { Page } from '@/components/Page'
import { AddAttributionEntryModal } from '@/components/dialogs/AddAttributionEntryModal'
import { RunDataExtractModal } from '@/components/dialogs/RunDataExtractModal'
import { PM } from '@/constants/links'
import { PERMISSIONS } from '@/constants/permissions'

type DataPresenceItem = {
  [key: string]: number
} & { key: string }

const dataPresenceCellRenderer = createCellRenderer<DataPresenceItem>([
  {
    field: 'present',
    check: (val) => val === 1,
    component: <Badge theme="success">Present</Badge>,
  },
  {
    field: 'missing',
    check: (val) => val === 0,
    component: <Badge theme="destructive">Missing</Badge>,
  },
])

const getColDefsFromData = (data: DataPresenceItem[]) => {
  const dates: string[] = []
  const today = toISO8601(new Date())

  for (let item of data) {
    const itemDates = Object.keys(item).filter((key) => !['key', 'groupName'].includes(key))

    for (let date of itemDates) {
      if (!dates.includes(date) && date !== today && !isBefore(new Date(date), getTMinus(6)))
        dates.push(date)
    }
  }

  return createColDefs<DataPresenceItem>([
    { field: 'groupName', rowGroup: true, hide: true },
    { field: 'key', headerName: 'Data', width: 100 },
    ...dates
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
      .map(
        (date) =>
          ({
            field: date,
            cellRenderer: dataPresenceCellRenderer,
          }) satisfies ColDef,
      ),
  ])
}

const processStateColDefs = createColDefs([
  {
    width: 10,
    field: 'id',
    hide: true,
  },
  {
    width: 100,
    field: 'run_id',
    rowGroup: true,
    hide: true,
  },
  {
    width: 160,
    field: 'upload_datetime_utc',
    headerName: 'Upload UTC',
    sort: 'desc',
    cellStyle: { textAlign: 'center' },
  },
  {
    width: 100,
    field: 'crashed',
    cellStyle: { textAlign: 'center' },
  },
  {
    width: 250,
    field: 'key',
    headerName: 'Name',
  },
  {
    width: 90,
    field: 'step',
    headerName: '#Step',
    cellStyle: { textAlign: 'center' },
  },
  {
    width: 500,
    field: 'output',
  },
])

export interface DataPresencePageProps {}

export const DataPresencePage: React.FC<DataPresencePageProps> = ({}) => {
  const [addingEntry, setAddingEntry] = useState(false)
  const [runningDataExtract, setRunningDataExtract] = useState(false)

  const api = useApi()

  const queries = useQueries({
    queries: [
      {
        queryKey: ['attributions', 'data-presence', 'banks-custodians'],
        queryFn: () => api.attributions.dataPresence.getBanksAttributions(),
        refetchInterval: 30_000,
      },
      {
        queryKey: ['attributions', 'data-presence', 'ops'],
        queryFn: () => api.attributions.dataPresence.getOpsAttributions(),
        refetchInterval: 30_000,
      },
      {
        queryKey: ['attributions', 'data-presence', 'prcoesses-state'],
        queryFn: () => api.attributions.dataPresence.getProcessState(),
        refetchInterval: 7_000,
      },
    ],
  })

  const [banksCustodians, ops, processState] = queries

  const [banksCustodiansData, opsData] = useMemo(() => {
    return [banksCustodians.data ?? [], ops.data ?? []].map((group, index) => {
      let groupName = index === 0 ? 'Banks & Custodians' : 'Funds'

      return group.reduce(
        (prev, curr) => {
          const date = curr.date
          const keysNotDate = Object.keys(curr).filter((key) => key !== 'date')

          for (let key of keysNotDate) {
            let item = prev.find((item) => item.key === key)

            if (!item) {
              // @ts-expect-error
              prev.push({ key, groupName, [date]: curr[key] })
            } else {
              // @ts-expect-error
              item[date] = curr[key]
            }
          }

          return prev
        },
        [] as unknown as DataPresenceItem[],
      )
    })
  }, [banksCustodians.data, ops.data])

  const banksCustodiansColDefs = useMemo(() => {
    return getColDefsFromData(banksCustodiansData)
  }, [banksCustodiansData])

  const query = useMemo(() => {
    return {
      refetch: async () => {
        return Promise.all([banksCustodians.refetch(), ops.refetch()])
      },
      dataUpdatedAt: [banksCustodians.dataUpdatedAt, ops.dataUpdatedAt].sort()?.[0],
      isFetching: [banksCustodians, ops].some((query) => query.isFetching),
      isLoading: [banksCustodians, ops].some((query) => query.isLoading),
    } satisfies WidgetHeaderWithQueryProps['query']
  }, [banksCustodians, ops])

  return (
    <>
      <AddAttributionEntryModal open={addingEntry} onOpenChange={setAddingEntry} />
      <RunDataExtractModal open={runningDataExtract} onOpenChange={setRunningDataExtract} />
      <Page
        metadata={{ title: 'Data Presence' }}
        permissions={PM.ATTRIBUTIONS.DATA_PRESENCE.permissions}
      >
        <CanAccess permissions={[PERMISSIONS.WRITE_DATA_PRESENCE]}>
          <HeaderConfigBar>
            <div className="flex items-center gap-2">
              <Button size="sm" variant={'outline'} onClick={() => setAddingEntry(true)}>
                <Plus />
                Add an entry
              </Button>
              <Button size="sm" variant={'outline'} onClick={() => setRunningDataExtract(true)}>
                <Play />
                Run Data Extract
              </Button>
            </div>
          </HeaderConfigBar>
        </CanAccess>
        <ResizablePanelGroup direction={'horizontal'}>
          <ResizablePanel>
            <Widget exposed className={'h-full'}>
              <WidgetHeaderWithQuery title={'Banks & Custodians Data Presence'} query={query} />
              <WidgetContent loading={query.isLoading}>
                <CustomAgGrid
                  domLayout={'normal'}
                  columnDefs={banksCustodiansColDefs}
                  rowData={[...banksCustodiansData, ...opsData]}
                />
              </WidgetContent>
            </Widget>
          </ResizablePanel>
          <ResizableHandle />
          <ResizablePanel>
            <Widget exposed className={'h-full'}>
              <WidgetHeaderWithQuery
                title={'Processes State - last 100 rows'}
                query={processState}
              />
              <WidgetContent loading={processState.isLoading}>
                <CustomAgGrid
                  domLayout={'normal'}
                  columnDefs={processStateColDefs}
                  rowData={processState.data}
                />
              </WidgetContent>
            </Widget>
          </ResizablePanel>
        </ResizablePanelGroup>
      </Page>
    </>
  )
}
